/**
 * Scans tab: searchable table of repository scans with score and drift.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Search, TrendingUp, TrendingDown, Minus, ExternalLink, RefreshCw } from 'lucide-react';
import type { Scan, Pagination } from './types';
import { scoreColor, scoreBg, gradeColor, formatDate } from './formatting';
import { Pager } from './helpers';

interface ScansTabProps {
  scans: Scan[];
  scansLoading: boolean;
  scansError: string | null;
  search: string;
  setSearch: (v: string) => void;
  scanPage: number;
  setScanPage: React.Dispatch<React.SetStateAction<number>>;
  scanPagination: Pagination | null;
  fetchScans: () => void;
}

export function ScansTab({
  scans,
  scansLoading,
  scansError,
  search,
  setSearch,
  scanPage,
  setScanPage,
  scanPagination,
  fetchScans,
}: ScansTabProps) {
  return (
    <div className="space-y-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setScanPage(0);
          fetchScans();
        }}
        className="flex gap-2"
      >
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search repos…"
            className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors"
        >
          Search
        </button>
      </form>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900">
        {scansLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : scansError ? (
          <div className="py-12 text-center text-red-500 text-sm">{scansError}</div>
        ) : scans.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">No scans found.</div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  <th className="px-4 py-3">Repository</th>
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3">Drift</th>
                  <th className="px-4 py-3">Scanned by</th>
                  <th className="px-4 py-3">Last scan</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                {scans.map((scan) => (
                  <tr
                    key={scan.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={scan.analysisId ? `/jobs/${scan.analysisId}/results` : '#'}
                        className="block"
                      >
                        <div className="font-medium text-gray-900 dark:text-white truncate max-w-xs hover:text-teal-600 dark:hover:text-teal-400 transition-colors">
                          {scan.fullName || scan.name}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5 capitalize">
                          {scan.platform} {scan.language && `· ${scan.language}`}
                        </div>
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <div
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-semibold ${scoreBg(scan.overallScore)}`}
                      >
                        <span className={`text-base ${scoreColor(scan.overallScore)}`}>
                          {scan.overallScore ?? '—'}
                        </span>
                        {scan.grade && (
                          <span className={`text-xs font-bold ${gradeColor(scan.grade)}`}>
                            {scan.grade}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {scan.scoreDrift === null ? (
                        <Minus className="w-4 h-4 text-gray-300" />
                      ) : scan.scoreDrift > 0 ? (
                        <span className="flex items-center gap-1 text-emerald-500 text-xs font-medium">
                          <TrendingUp className="w-3.5 h-3.5" />+{scan.scoreDrift}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-500 text-xs font-medium">
                          <TrendingDown className="w-3.5 h-3.5" />
                          {scan.scoreDrift}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                      {scan.scannedBy?.email ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                      {formatDate(scan.analyzedAt)}
                    </td>
                    <td className="px-4 py-3">
                      <ExternalLink className="w-3.5 h-3.5 text-gray-300 hover:text-teal-500 transition-colors" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pager
              page={scanPage}
              pagination={scanPagination}
              onPrev={() => setScanPage((p) => Math.max(0, p - 1))}
              onNext={() => setScanPage((p) => p + 1)}
            />
          </>
        )}
      </div>
    </div>
  );
}
